import { Send } from 'lucide-react';
import { AutoResizeTextarea } from '@lightning/ui';
import { cn } from '@/lib/utils';

interface HowieComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void | Promise<void>;
  disabled?: boolean;
  sending?: boolean;
  allowSendWhileSending?: boolean;
  placeholder?: string;
  className?: string;
  minRows?: number;
}

export function HowieComposer({
  value,
  onChange,
  onSend,
  disabled = false,
  sending = false,
  allowSendWhileSending = false,
  placeholder = 'Ask Howie...',
  className,
  minRows = 1,
}: HowieComposerProps) {
  const hasInput = value.trim().length > 0;
  // Keep the composer usable while a reply is streaming. Sending another
  // message at that point steers the active turn instead of dropping input.
  const isDisabled = disabled;
  const sendDisabled = isDisabled || (sending && !allowSendWhileSending);

  const submitComposer = () => {
    if (!hasInput || sendDisabled) return;
    void onSend();
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        submitComposer();
      }}
      className={cn(
        'relative flex items-center rounded-2xl border border-black/5 bg-white px-4 py-3 shadow-composer transition-[border-color,box-shadow] focus-within:border-how-rose focus-within:ring-2 focus-within:ring-how-space-deep/20',
        className
      )}
    >
      <AutoResizeTextarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            submitComposer();
          }
        }}
        placeholder={placeholder}
        minRows={minRows}
        maxRows={6}
        disabled={isDisabled}
        aria-label="Ask Howie"
        className="min-h-10 w-full flex-1 border-0 bg-transparent py-2 pl-0 pr-12 text-left font-body text-[15px] leading-6 text-how-space-deep shadow-none outline-none placeholder:text-left placeholder:text-gray-400 focus-visible:ring-0 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50"
      />
      <button
        type="submit"
        disabled={!hasInput || sendDisabled}
        className={cn(
          'absolute bottom-3 right-3 inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep focus-visible:ring-offset-2 disabled:cursor-not-allowed',
          hasInput && !sendDisabled
            ? 'bg-how-accent-warm text-white hover:bg-how-red-hover'
            : 'bg-gray-100 text-gray-400'
        )}
        aria-label={sending && allowSendWhileSending ? 'Steer response' : 'Send message'}
      >
        <Send size={16} />
      </button>
    </form>
  );
}
